import { useState, useEffect, useCallback, useMemo } from "react";
import { getLeadDocuments, getDocumentVerificationHistory } from "services/leadsService";

const fetchDocuments = async (params) => {
  try {
    const isHistoryView = params.viewType === "history";
    const apiCall = isHistoryView ? getDocumentVerificationHistory : getLeadDocuments;
    
    const response = await apiCall({
      leadId: params.leadId,
      fileType: params.fileType,
      search: params.search,
      startDate: params.startDate,
      endDate: params.endDate,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    });

    if (response.success) {
      const transformedData = response.data.map((doc) => {
        if (isHistoryView) {
          // Map history data structure - preserve the History array
          return {
            id: doc.DocumentId,
            name: doc.FileName,
            fileName: doc.FileName,
            filePath: doc.FilePath,
            size:  doc.FileSize * 1024,
            fileType: doc.FileName?.split('.').pop()?.toLowerCase() || 'unknown',
            uploadedAt: doc.UploadedOn,
            uploadedBy: doc.UploadedByName,
            verificationStatus: doc.VerificationStatus,
            remarks: doc.Remark,
            canApproveOrReject: doc.CanApproveOrReject,
            leadName: doc.LeadName,
            isHistoryItem: true,
            leadId: params.leadId,
            // Preserve the original document structure for history view
            DocumentId: doc.DocumentId,
            FileName: doc.FileName,
            FileExtension: doc.FileExtension,
            FilePath: doc.FilePath,
            LeadName: doc.LeadName,
            History: doc.History || [],
          };
        } else {
          // Map regular documents data structure
          return {
            id: doc.DocumentId,
            name: doc.FileName,
            fileType: doc.FileName.split(".").pop().toLowerCase(),
            size: doc.FileSize * 1024,
            uploadedBy: doc.UploadedByName,
            uploadedAt: doc.UploadedOn,
            leadId: params.leadId,
            filePath: doc.FilePath,
            isVerified: doc.IsVerified ?? doc.IsVerfied ?? false,
            docVerificationId: doc.DocVerificationId,
            canApproveOrReject: doc.CanApproveOrReject,
            verificationStatus:
              typeof doc.VerificationStatus === "number"
                ? doc.VerificationStatus
                : parseInt(doc.VerificationStatus ?? 0, 10) || 0,
            verifiedBy:
              doc.VerifiedByName || doc.VerifiedBy || doc.VerifiedById || null,
            verifiedAt: doc.VerifiedAt || doc.VerifiedDate || null,
            verificationRemark:
              doc.Remark || doc.Remarks || doc.VerificationRemark || "",
            isHistoryItem: false,
          };
        }
      });

      return {
        data: transformedData,
        totalCount: response.totalRecords,
        hasMore: transformedData.length >= params.pageSize,
      };
    } else {
      throw new Error(response.error);
    }
  } catch (error) {
    console.error("Error fetching documents:", error);
    return {
      data: [],
      totalCount: 0,
      hasMore: false,
    };
  }
};

export const useDocuments = (leadId, initialViewType = "documents") => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasInitialData, setHasInitialData] = useState(false);
  const [viewType, setViewType] = useState(initialViewType);

  const [filterState, setFilterState] = useState({
    leadId: leadId,
    fileType: null,
    search: "",
    startDate: null,
    endDate: null,
    pageNumber: 1,
    pageSize: 10,
    viewType: initialViewType,
  });

  const hasActiveFilters = useMemo(() => {
    return (
      filterState.fileType ||
      filterState.search ||
      filterState.startDate ||
      filterState.endDate
    );
  }, [
    filterState.fileType,
    filterState.search,
    filterState.startDate,
    filterState.endDate,
  ]);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchDocuments(filterState);
      setDocuments(response.data);
      setTotalRecords(response.totalCount);

      if (!hasActiveFilters && !hasInitialData) {
        setHasInitialData(response.totalCount > 0);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  }, [filterState, hasActiveFilters, hasInitialData]);

  const refetch = useCallback(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    loadDocuments();
  }, [filterState]);

  const handleFilterChange = (key, value) => {
    setFilterState((prev) => ({
      ...prev,
      [key]: value === "" ? null : value,
      pageNumber: 1,
    }));
  };

  const handlePageChange = (page) => {
    setFilterState((prev) => ({ ...prev, pageNumber: page }));
  };

  const clearFilters = () => {
    setFilterState(prev => ({
      ...prev,
      fileType: null,
      search: "",
      startDate: null,
      endDate: null,
      pageNumber: 1,
    }));

    const searchInput = document.querySelector(
      'input[placeholder="Search documents..."]',
    );
    if (searchInput) searchInput.value = "";
  };

  const switchView = (newViewType) => {
    setViewType(newViewType);
    setFilterState(prev => ({
      ...prev,
      viewType: newViewType,
      fileType: null,
      search: "",
      startDate: null,
      endDate: null,
      pageNumber: 1,
    }));
    
    // Clear search input
    const searchInput = document.querySelector(
      'input[placeholder="Search documents..."]',
    );
    if (searchInput) searchInput.value = "";
  };

  return {
    documents,
    loading,
    totalRecords,
    hasInitialData,
    hasActiveFilters,
    filterState,
    handleFilterChange,
    handlePageChange,
    clearFilters,
    switchView,
    viewType,
    totalPages: Math.ceil(totalRecords / filterState.pageSize),
    refetch,
  };
};
